import React, { useRef, useEffect } from 'react';
import { Group, Rect } from 'react-konva';
import { GroupShape as IGroupShape } from '../types';
import { getSnapPosition } from '../utils/snap';
import { SnapLines } from './SnapLines';
import { useMapCanvas } from './map/contexts/MapCanvasContext';
import { DraggableShape } from './map/shapes/DraggableShape/DraggableShape';
import { ShapeProvider } from './map/shapes/ShapeContext/ShapeProvider';
import { useShape } from './map/shapes/ShapeContext/useShape';
import { ShapeTransformer } from './map/shapes/ShapeTransformer';
import { ShapeCommonProps } from './map/types';

export const GroupShape: React.FC<ShapeCommonProps> = (commonProps) => {
  const {
    setSnapLines,
    snapLines,
    shape,
    isSelected,
    onSelect,
    onUpdate,
    editorConfigRef,
    otherShapes,
    disabled
  } = useShape();

  const { isViewMode, shapesRef } = useMapCanvas();

  const groupShape = shape as IGroupShape;

  const groupRef = useRef<any>(null);
  const transformerRef = useRef<any>(null);

  useEffect(() => {
    if (isSelected && transformerRef.current && groupRef.current) {
      transformerRef.current.nodes([groupRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, shape]);

  const snapToGridValue = (value: number): number => {
    const cfg = editorConfigRef?.current;
    if (!cfg?.snapToGrid) return value;
    return Math.round(value / cfg.gridSize) * cfg.gridSize;
  };

  const handleDragMove = (e: any) => {
    if (disabled) return;

    const node = e.target;
    let newX = node.x();
    let newY = node.y();

    const cfg = editorConfigRef?.current;

    if (cfg?.showSnapLines) {
      const stageScale = node.getStage()?.scaleX() ?? 1;
      // Read shapes fresh from the ref so snap sees the latest state
      // (e.g., composite templates applied after this component rendered).
      const liveShapes = shapesRef?.current || otherShapes || [];
      const snapResult = getSnapPosition(
        shape,
        liveShapes,
        { x: newX, y: newY },
        groupShape.id,
        stageScale
      );

      if (snapResult.position.x !== undefined) {
        newX = snapResult.position.x;
      }
      if (snapResult.position.y !== undefined) {
        newY = snapResult.position.y;
      }

      setSnapLines({
        x: snapResult.position.x !== undefined ? snapResult.lines.x : undefined,
        y: snapResult.position.y !== undefined ? snapResult.lines.y : undefined,
        targetX: snapResult.targetDot.x,
        targetY: snapResult.targetDot.y
      });
    } else {
      setSnapLines({});
    }

    // Grid snap runs last so the final position is always grid-aligned.
    if (cfg?.snapToGrid) {
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
    }

    node.position({ x: newX, y: newY });
  };

  const handleDragEnd = () => {
    if (!disabled) {
      const node = groupRef.current;
      onUpdate({
        x: node.x(),
        y: node.y()
      });
    }
    setSnapLines({});
  };

  const handleTransformEnd = () => {
    if (!disabled) {
      const node = groupRef.current;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();

      node.scaleX(1);
      node.scaleY(1);

      onUpdate({
        x: node.x(),
        y: node.y(),
        width: Math.max(5, node.width() * scaleX),
        height: Math.max(5, node.height() * scaleY)
      });
    }
  };

  const handleChildClick = (e: any) => {
    e.cancelBubble = true;
    onSelect(e);
  };

  return (
    <>
      {/* @ts-ignore*/}
      <Group ref={groupRef} {...commonProps}>
        <Rect
          width={groupShape.width}
          height={groupShape.height}
          stroke="transparent"
          fill="transparent"
        />
        {groupShape.children.map((childShape) => (
          <ShapeProvider
            key={childShape.id}
            shape={childShape}
            isSelected={false}
            onSelect={handleChildClick}
            onUpdate={() => {}}
            isGroupChild={true}
            parentGroupColor={groupShape.color}
            editorConfigRef={editorConfigRef}
            otherShapes={[
              ...(otherShapes || []),
              ...groupShape.children.filter((c) => c.id !== childShape.id)
            ]}
            disabled={disabled}
          >
            <DraggableShape key={childShape.id} />
          </ShapeProvider>
        ))}
      </Group>
      {isSelected && !disabled && <ShapeTransformer ref={transformerRef} />}
      {editorConfigRef?.current?.showSnapLines && <SnapLines snapLines={snapLines} />}
    </>
  );
};
